import React, { useEffect, useState } from "react";
import { Calendar, CloudSun, MapPin, LocateFixed } from "lucide-react";
import api from "@/lib/api";

const WEATHER_CODES = {
  0: { icon: "☀️", text: "Slunečno" },
  1: { icon: "🌤️", text: "Skoro jasno" },
  2: { icon: "⛅", text: "Polojasno" },
  3: { icon: "☁️", text: "Zataženo" },
  45: { icon: "🌫️", text: "Mlha" },
  48: { icon: "🌫️", text: "Námrazová mlha" },
  51: { icon: "🌦️", text: "Mrholení" },
  53: { icon: "🌦️", text: "Mrholení" },
  55: { icon: "🌦️", text: "Mrholení" },
  61: { icon: "🌧️", text: "Slabý déšť" },
  63: { icon: "🌧️", text: "Déšť" },
  65: { icon: "🌧️", text: "Silný déšť" },
  71: { icon: "🌨️", text: "Slabé sněžení" },
  73: { icon: "🌨️", text: "Sněžení" },
  75: { icon: "❄️", text: "Silné sněžení" },
  77: { icon: "🌨️", text: "Sněhové vločky" },
  80: { icon: "🌦️", text: "Přeháňky" },
  81: { icon: "🌧️", text: "Přeháňky" },
  82: { icon: "⛈️", text: "Silné přeháňky" },
  85: { icon: "🌨️", text: "Sněhové přeháňky" },
  86: { icon: "❄️", text: "Sněhové přeháňky" },
  95: { icon: "⛈️", text: "Bouřka" },
  96: { icon: "⛈️", text: "Bouřka s krupobitím" },
  99: { icon: "⛈️", text: "Silná bouřka" },
};

const CITIES = {
  praha: { name: "Praha", lat: 50.0755, lon: 14.4378 },
  brno: { name: "Brno", lat: 49.1951, lon: 16.6068 },
  ostrava: { name: "Ostrava", lat: 49.8209, lon: 18.2625 },
  plzen: { name: "Plzeň", lat: 49.7384, lon: 13.3736 },
  liberec: { name: "Liberec", lat: 50.7671, lon: 15.0562 },
  olomouc: { name: "Olomouc", lat: 49.5938, lon: 17.2509 },
  cb: { name: "České Budějovice", lat: 48.9745, lon: 14.4744 },
  hk: { name: "Hradec Králové", lat: 50.2104, lon: 15.8252 },
  pardubice: { name: "Pardubice", lat: 50.0343, lon: 15.7812 },
};

function formatDateCs(d) {
  const days = ["neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota"];
  return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

async function reverseGeocode(lat, lon) {
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&language=cs&count=1`;
    const r = await fetch(url);
    const d = await r.json();
    const res = d?.results?.[0];
    if (res) return res.name || res.admin1 || null;
  } catch (_) {}
  return null;
}

export default function InfoBar() {
  const [now, setNow] = useState(new Date());
  const [day, setDay] = useState(null);
  const [weather, setWeather] = useState(null);
  // mode: 'geo' (live), 'city' (manual), 'fallback' (no geo permission yet)
  const initialMode = localStorage.getItem("stb_loc_mode") || "geo";
  const initialCity = localStorage.getItem("stb_city") || "praha";
  const [mode, setMode] = useState(initialMode);
  const [cityKey, setCityKey] = useState(initialCity);
  const [geo, setGeo] = useState(() => {
    try {
      const raw = localStorage.getItem("stb_geo");
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });
  const [geoError, setGeoError] = useState("");

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Name day
  useEffect(() => {
    let cancel = false;
    api.get("/today").then((r) => { if (!cancel) setDay(r.data?.day); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  // Request geolocation on mount if user is in geo mode
  useEffect(() => {
    if (mode !== "geo") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setMode("city");
      localStorage.setItem("stb_loc_mode", "city");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const name = (await reverseGeocode(lat, lon)) || "Tvá poloha";
        const next = { lat, lon, name };
        setGeo(next);
        localStorage.setItem("stb_geo", JSON.stringify(next));
        setGeoError("");
      },
      (err) => {
        setGeoError(err.message || "geo");
        setMode("city");
        localStorage.setItem("stb_loc_mode", "city");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30 * 60 * 1000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active point (lat/lon)
  const active = mode === "geo" && geo
    ? { lat: geo.lat, lon: geo.lon, name: geo.name }
    : { lat: (CITIES[cityKey] || CITIES.praha).lat, lon: (CITIES[cityKey] || CITIES.praha).lon, name: (CITIES[cityKey] || CITIES.praha).name };

  // Weather
  useEffect(() => {
    let cancel = false;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${active.lat}&longitude=${active.lon}&current=temperature_2m,weather_code&timezone=Europe%2FPrague`;
    fetch(url).then((r) => r.json()).then((d) => {
      if (cancel) return;
      const cur = d?.current;
      if (cur) {
        setWeather({
          temp: Math.round(cur.temperature_2m),
          code: cur.weather_code,
        });
      }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [active.lat, active.lon]);

  const useGeo = () => {
    setMode("geo");
    localStorage.setItem("stb_loc_mode", "geo");
    setGeoError("");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          const name = (await reverseGeocode(lat, lon)) || "Tvá poloha";
          const next = { lat, lon, name };
          setGeo(next);
          localStorage.setItem("stb_geo", JSON.stringify(next));
        },
        (err) => setGeoError(err.message || "geo"),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 30 * 60 * 1000 },
      );
    }
  };

  const onPickCity = (k) => {
    setCityKey(k);
    localStorage.setItem("stb_city", k);
    setMode("city");
    localStorage.setItem("stb_loc_mode", "city");
  };

  const w = weather ? WEATHER_CODES[weather.code] || { icon: "🌡️", text: "—" } : null;
  const time = now.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      className="bg-[#0E0E0E] text-[#E5E5E5] text-xs border-b border-[#262626]"
      data-testid="info-bar"
    >
      <div className="stb-container py-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex items-center gap-1.5 stb-mono">
          <Calendar size={12} className="text-[#FF6B00]" />
          <span>{formatDateCs(now)}</span>
          <span className="text-[#FF6B00]">·</span>
          <span>{time}</span>
        </div>

        {day && (
          <div className="flex items-center gap-1.5" data-testid="nameday">
            <span className="text-[#FF6B00] stb-mono">Svátek:</span>
            <span className="font-medium">{day.name}</span>
            {day.isHoliday && day.holidayName && (
              <span className="stb-chip text-[#FFB800] border-[#FFB800] !py-0">
                {day.holidayName}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5" data-testid="weather">
          <CloudSun size={12} className="text-[#FF6B00]" />
          {w ? (
            <>
              <span>{w.icon}</span>
              <span>{weather.temp}°C</span>
              <span className="hidden sm:inline text-[#A3A3A3]">{w.text}</span>
            </>
          ) : (
            <span className="text-[#A3A3A3]">— —</span>
          )}
          <span className="text-[#666] mx-1">·</span>
          <MapPin size={11} className="text-[#A3A3A3]" />
          {mode === "geo" && geo ? (
            <button
              type="button"
              onClick={useGeo}
              className="stb-mono text-[#E5E5E5] hover:text-[#FF6B00] inline-flex items-center gap-1"
              title="Aktualizovat polohu"
              data-testid="geo-active"
            >
              <LocateFixed size={11} className="text-[#FF6B00]" />
              <span>{geo.name}</span>
            </button>
          ) : (
            <>
              <select
                value={cityKey}
                onChange={(e) => onPickCity(e.target.value)}
                className="bg-transparent text-[#E5E5E5] border-none stb-mono text-xs cursor-pointer focus:outline-none"
                data-testid="city-select"
              >
                {Object.entries(CITIES).map(([k, c]) => (
                  <option key={k} value={k} className="bg-[#0E0E0E]">{c.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={useGeo}
                className="text-[#A3A3A3] hover:text-[#FF6B00] inline-flex items-center"
                title="Použít moji polohu"
                data-testid="geo-btn"
              >
                <LocateFixed size={12} />
              </button>
            </>
          )}
        </div>
      </div>
      {geoError && mode === "city" && (
        <div className="stb-container pb-1 -mt-0.5 stb-mono text-[10px] text-[#FFB800]">
          Geolokace nedostupná – použit výběr města.
        </div>
      )}
    </div>
  );
}
